import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarClock, Rocket, AlertTriangle, CheckCircle2, Hourglass } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';

const PACE_STYLE = {
  'on-track': { icon: CheckCircle2, label: 'ON PACE', color: '#00FFD1', storyColor: '#2B5329' },
  'behind':   { icon: AlertTriangle, label: 'BEHIND', color: '#FF8A00', storyColor: '#8C1C13' },
  'early':    { icon: Rocket, label: 'EARLY',  color: '#00FFD1', storyColor: '#2B5329' },
  'final':    { icon: Hourglass, label: 'FINAL DAYS', color: '#FF3366', storyColor: '#8C1C13' },
  'past':     { icon: AlertTriangle, label: 'PAST',   color: '#666666', storyColor: '#68615A' },
};

export default function TestDayCountdown({ variant = 'cyber' }) {
  const { user, API, mode } = useApp();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const isCyber = variant === 'cyber';

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('pp-token') || '';
    axios.get(`${API}/countdown/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data)).catch(() => setData(null));
  }, [user, mode]);

  if (!user) return null;

  if (data && !data.has_plan) {
    return (
      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
        data-testid="countdown-empty"
        className={`w-full max-w-3xl rounded-2xl px-5 py-3 flex items-center justify-between gap-4 ${
          isCyber ? 'glass-cyber' : 'parchment'
        }`}>
        <div className="flex items-center gap-3">
          <CalendarClock size={20} className={isCyber ? 'text-[#00FFD1]' : 'text-[#8C1C13]'} />
          <div>
            <div className={isCyber ? 'font-mono-cyber text-[10px] text-[#00FFD1] tracking-widest' : 'font-body-story italic text-[#68615A] text-xs'}>
              {isCyber ? '// no test date on file' : 'No test date set yet'}
            </div>
            <div className={isCyber ? 'font-orbitron text-sm' : 'font-storybook text-lg'}>
              {isCyber ? 'SET TEST DATE // BUILD PLAN' : 'Chart your journey to see the countdown'}
            </div>
          </div>
        </div>
        <button data-testid="countdown-build-plan" onClick={() => nav('/study-plan')}
          className={isCyber ? 'px-4 py-1.5 bg-[#00FFD1] text-black text-xs font-orbitron tracking-widest rounded-full' : 'storybook-btn px-4 py-1.5 rounded-full text-xs'}>
          {isCyber ? 'BUILD PLAN' : 'Chart Journey'}
        </button>
      </motion.div>
    );
  }

  if (!data) return null;

  const pace = PACE_STYLE[data.pace] || PACE_STYLE['on-track'];
  const Icon = pace.icon;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }}
        data-testid="countdown-ribbon"
        className={`w-full max-w-3xl rounded-2xl px-5 py-4 flex items-center gap-4 ${
          isCyber ? 'glass-cyber cyber-glow' : 'parchment'
        }`}>
        <Icon size={26} style={{ color: isCyber ? pace.color : pace.storyColor }} />
        <div className="flex-1">
          <div className={isCyber ? 'font-mono-cyber text-[10px] text-[#00FFD1] tracking-widest' : 'font-body-story italic text-[#68615A] text-xs'}>
            {isCyber ? `// TEST_DATE: ${data.test_date}` : `Test date: ${data.test_date}`}
          </div>
          <div className={isCyber ? 'font-orbitron text-2xl tracking-wider' : 'font-storybook text-3xl'}>
            <span data-testid="countdown-days" style={{ color: isCyber ? pace.color : pace.storyColor }}>
              {Math.max(0, data.days_remaining)}
            </span>{' '}
            <span className={isCyber ? 'text-white' : ''}>days</span>{' '}
            <span className={isCyber ? 'font-mono-cyber text-sm text-neutral-400' : 'font-body-story text-base text-[#68615A]'}>
              · {data.hours_remaining}h
            </span>
          </div>
          <div data-testid="countdown-nudge" className={isCyber ? 'text-xs text-neutral-300 mt-0.5' : 'text-sm text-[#4A413A] mt-0.5'}>
            {data.nudge}
          </div>
        </div>
        <div className={`px-3 py-1 rounded-full text-[10px] font-bold tracking-widest ${
          isCyber ? 'font-mono-cyber' : 'font-storybook'
        }`}
          style={{
            background: isCyber ? `${pace.color}22` : `${pace.storyColor}22`,
            color: isCyber ? pace.color : pace.storyColor,
            border: `1px solid ${isCyber ? pace.color : pace.storyColor}66`,
          }}
          data-testid="countdown-pace">
          {pace.label}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
